import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Radio, Lock, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LiveNowPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [liveStream, setLiveStream] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    fetchLiveStream();
  }, []);

  useEffect(() => {
    if (!authLoading) {
      setHasAccess(user && user.subscription_status === 'active');
    }
  }, [user, authLoading]);

  const fetchLiveStream = async () => {
    try {
      const response = await axios.get(`${API}/live/stream`);
      setLiveStream(response.data);
    } catch (error) {
      console.error('Failed to fetch live stream:', error);
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-slate-950 text-white">
        <Navbar />
        <div className="flex items-center justify-center min-h-[calc(100vh-64px)]">
          <p className="text-xl text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  // No live stream currently
  if (!liveStream) {
    return (
      <div className="min-h-screen bg-slate-950 text-white">
        <Navbar />
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 py-24 text-center">
          <AlertCircle className="w-20 h-20 text-slate-600 mx-auto mb-8" />
          <h1 
            data-testid="no-stream-heading"
            className="text-4xl sm:text-5xl font-bold mb-6"
            style={{ fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase' }}
          >
            No Live Stream Currently
          </h1>
          <p className="text-xl text-slate-400 mb-8" style={{ fontFamily: 'Inter, sans-serif' }}>
            We're not live right now. Check back soon for our next broadcast!
          </p>
          <div className="flex gap-4 justify-center">
            <Link to="/live">
              <Button className="bg-red-600 hover:bg-red-700 text-white" style={{ fontFamily: 'Oswald, sans-serif' }}>
                Watch Recorded Episodes
              </Button>
            </Link>
            <Link to="/">
              <Button variant="outline" className="border-slate-700 text-white hover:bg-slate-800" style={{ fontFamily: 'Oswald, sans-serif' }}>
                Return Home
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // User doesn't have premium access
  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-slate-950 text-white">
        <Navbar />
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 py-24 text-center">
          <Lock className="w-20 h-20 text-red-600 mx-auto mb-8" />
          <div className="inline-block mb-6 px-4 py-2 bg-red-600 text-white rounded-sm">
            <Radio className="w-5 h-5 inline mr-2" />
            <span className="font-bold" style={{ fontFamily: 'Oswald, sans-serif' }}>LIVE NOW</span>
          </div>
          <h1 
            data-testid="live-locked-heading"
            className="text-4xl sm:text-5xl font-bold mb-6"
            style={{ fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase' }}
          >
            {liveStream.title}
          </h1>
          <p className="text-xl text-slate-400 mb-8" style={{ fontFamily: 'Inter, sans-serif' }}>
            This exclusive live broadcast is available only to premium subscribers.
          </p>
          
          {user ? (
            <Link to="/pricing" data-testid="upgrade-button">
              <Button 
                className="bg-red-600 hover:bg-red-700 text-white text-lg px-8 py-6 rounded-sm font-bold"
                style={{ fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase' }}
              >
                Upgrade to Watch Live
              </Button>
            </Link>
          ) : (
            <div className="space-y-4">
              <p className="text-slate-300" style={{ fontFamily: 'Inter, sans-serif' }}>Sign in or create an account to subscribe</p>
              <div className="flex gap-4 justify-center">
                <Link to="/login">
                  <Button variant="outline" className="border-slate-700 text-white hover:bg-slate-800" style={{ fontFamily: 'Oswald, sans-serif' }}>
                    Login
                  </Button>
                </Link>
                <Link to="/register" data-testid="get-started-button">
                  <Button 
                    className="bg-red-600 hover:bg-red-700 text-white"
                    style={{ fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase' }}
                  >
                    Get Started
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Premium user - show live stream
  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <Navbar />
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 py-12">
        <div className="mb-8">
          <div className="inline-block mb-4 px-4 py-2 bg-red-600 text-white rounded-sm animate-pulse">
            <Radio className="w-5 h-5 inline mr-2" />
            <span className="font-bold" style={{ fontFamily: 'Oswald, sans-serif' }}>LIVE NOW</span>
          </div>
          <h1 
            data-testid="live-stream-heading"
            className="text-4xl sm:text-5xl font-bold mb-4"
            style={{ fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase' }}
          >
            {liveStream.title}
          </h1>
          <p className="text-xl text-slate-400" style={{ fontFamily: 'Inter, sans-serif' }}>
            {liveStream.description}
          </p>
        </div>

        <div data-testid="live-stream-player" className="aspect-video bg-slate-900 rounded-sm overflow-hidden mb-6 border border-slate-800">
          <iframe
            width="100%"
            height="100%"
            src={`https://www.youtube.com/embed/${liveStream.youtube_video_id}?autoplay=1`}
            title={liveStream.title}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
            className="w-full h-full"
          />
        </div>

        <div className="bg-slate-900 p-6 rounded-sm border border-slate-800">
          <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Oswald, sans-serif' }}>About This Stream</h2>
          <p className="text-slate-300 mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
            {liveStream.description}
          </p>
          <p className="text-sm text-slate-500" style={{ fontFamily: 'Inter, sans-serif' }}>
            This is an exclusive live broadcast for premium subscribers. Thank you for your support!
          </p>
        </div>
      </div>
    </div>
  );
};

export default LiveNowPage;
